import React, { forwardRef } from 'react';
import classNames from 'classnames';
import {
    CollapsiblePaneLayout,
    ModalContent,
    ModalContentChildren,
    PaneLayoutInteractionType,
} from '@eventbrite/eds-modal';
import PageFooter from '../../components/PageFooter';
import LanguageAndBrand from '../../components/languageAndBrand';
import ReservedPane from '../pane/ReservedPane';
import Pane from '../pane/Pane';
import { connectPane } from '../pane/connectPane';
import { getDisplayClasses } from '../../utils/displayClasses';
import './pageTemplate.scss';

const ConnectedPaneComponent = connectPane(Pane);
const ConnectedReservedPaneComponent = connectPane(ReservedPane);

const PageTemplate = forwardRef(
    (
        {
            children,
            collapsiblePaneNode,
            containerClassName,
            ctaNode,
            ctaText,
            ctaType = 'fill',
            extraModalBodyProps,
            extraPaneProps,
            headerActionIconType,
            isPaneOpen = false,
            onPaneCloseIntent,
            isSubmitDisabled = false,
            paneInteraction = PaneLayoutInteractionType.asCollapsible,
            keepDisabledStyleWhileLoading = false,
            handleSubmit,
            hasTopMargin = true,
            hasHorizontalGutters = true,
            headerActionTitle,
            notificationOptions,
            onPaneToggle,
            onHeaderAction,
            secondaryTitle,
            templateProps = {},
            fullWidthTopContent,
            fullWidthBottomContent,
            title,
            dataSpec,
            tertiaryNode,
            footerMessageNode,
            modalFooterNode,
            fullPageLayerNode,
            urgencySignalNode,
            showCartSummaryButton = true,
            paneAriaHidden,
        },
        ref,
    ) => {
        const { isReservedSeating, totalTicketQuantity, isSubmitting } =
            templateProps;
        const PaneComponent = isReservedSeating
            ? ConnectedReservedPaneComponent
            : ConnectedPaneComponent;
        const containerClasses = hasHorizontalGutters
            ? classNames(
                  getDisplayClasses({ hasTopMargin }),
                  containerClassName,
              )
            : containerClassName;

        const content = (
            <ModalContent
                title={title}
                secondaryTitle={secondaryTitle}
                notificationOptions={notificationOptions}
                headerActionIconType={headerActionIconType}
                headerActionTitle={headerActionTitle}
                onHeaderAction={onHeaderAction}
                {...extraModalBodyProps}
            >
                <ModalContentChildren>
                    {fullWidthTopContent}
                    <div
                        className={containerClasses}
                        data-spec={dataSpec}
                        ref={ref}
                    >
                        {children}
                    </div>
                    {fullWidthBottomContent}
                </ModalContentChildren>
                <PageFooter
                    ctaText={ctaText}
                    ctaNode={ctaNode}
                    ctaType={ctaType}
                    urgencySignalNode={urgencySignalNode}
                    footerMessageNode={footerMessageNode}
                    paneInteraction={paneInteraction}
                    handleSubmit={handleSubmit}
                    onPaneToggle={onPaneToggle}
                    modalFooterNode={modalFooterNode}
                    showCartSummaryButton={showCartSummaryButton}
                    keepDisabledStyleWhileLoading={
                        keepDisabledStyleWhileLoading
                    }
                    isSubmitDisabled={isSubmitDisabled}
                    isSubmitting={isSubmitting}
                    isPaneOpen={isPaneOpen}
                    tertiaryNode={tertiaryNode}
                    onPaneCloseIntent={onPaneCloseIntent}
                    shouldAlignLeft={false}
                />
            </ModalContent>
        );

        const paneForSummaryButton = showCartSummaryButton ? (
            <PaneComponent
                {...extraPaneProps}
                onPaneCloseButtonClick={onPaneCloseIntent}
                paneInteraction={paneInteraction}
                ticketQuantity={totalTicketQuantity}
            />
        ) : null;
        const pane = collapsiblePaneNode ?? paneForSummaryButton;

        return pane ? (
            <>
                <CollapsiblePaneLayout
                    pane={pane}
                    isPaneOpen={isPaneOpen}
                    onPaneClickOutside={onPaneCloseIntent}
                    paneAriaHidden={paneAriaHidden}
                    paneInteraction={paneInteraction}
                >
                    {content}
                </CollapsiblePaneLayout>
                {fullPageLayerNode}
            </>
        ) : (
            content
        );
    },
);

export default PageTemplate;
